import { DropdownButtonType, FilteringList, FilteringListProps, List, ListItem, SingleSelectDropdown, SingleSelectDropdownProps } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { ListResult } from '../../ts/fetchers/ListResult';
import { ISliceable } from '../../ts/models/ISliceable';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { IGroup } from '../../ts/ServerApi/types/IGroup';
import { IUserName } from '../../ts/ServerApi/types/IUser';
import { SearchSuggestionsService } from '../../ts/services/SearchSuggestionsService';

export enum GranteeType {
  user = 'user',
  group = 'group',
}

export const mapGranteeTypeToDisplayText: () => {[key: string]: string} = () => {
  return {
    [GranteeType.user]: Messages.user(),
    [GranteeType.group]: Messages.group(),
  };
};

export interface AddNewPermissionProps {
  searchSuggestionsService: SearchSuggestionsService;
}

export interface AddNewPermissionState {
  isButton: boolean;
  selectedGranteeType: GranteeType;
  searchTerm: string;
  searchResults: ListItem[];
}

export class AddNewPermission extends React.Component<AddNewPermissionProps, AddNewPermissionState> {
  public static readonly debounceMilliseconds = 250;

  public state: AddNewPermissionState = {
    isButton: true, // default to show button
    selectedGranteeType: GranteeType.user, // default to user
    searchTerm: '',
    searchResults: [],
  };

  private clearSearchState = (): void => {
    this.setState({
      searchTerm: '',
      searchResults: [],
    });
  }

  private foundNoMatchingResults = (): void => {
    this.setState({ searchResults: [{ value: Messages.noMatchingResults(), disabled:true }] });
  }

  private searchUsers = (text: string): void => {
    const query: ResourceQuery<IUserName> = this.props.searchSuggestionsService.users(text);
    query.getCount().then((count: number) => {
      count === 0 ? this.foundNoMatchingResults() :
      query.getSlice(0, count).then((names: ListResult<IUserName>) => {
        if (text === this.state.searchTerm) {
          this.setState({
            searchResults: names.items.map((item: IUserName) => {
              return {
                value: item.id,
                content: item.displayName,
              };
            }),
          });
        }
      });
    });
  }

  private debounceSearchUsers = _.debounce(this.searchUsers, AddNewPermission.debounceMilliseconds);

  private searchGroups = (text: string): void => {
    const slice: ISliceable<IGroup> = this.props.searchSuggestionsService.groups(text);
    slice.getSlice(0, 0).then((result: ListResult<IGroup>) => {
      result.total === 0 ? this.foundNoMatchingResults() :
      slice.getSlice(0, result.total).then((names: ListResult<IGroup>) => {
        if (text === this.state.searchTerm) {
          this.setState({
            searchResults: names.items.map((item: IGroup) => {
              return {
                value: item.id,
                content: item.name,
              };
            }),
          });
        }
      });
    });
  }

  private debounceSearchGroups = _.debounce(this.searchGroups, AddNewPermission.debounceMilliseconds);

  private toggleButtonVisibility = (): void => {
    this.setState((prevState) => {
      return { isButton: !prevState.isButton };
    });
  }

  private onGranteeTypeChange = (value: GranteeType): void => {
    if (this.state.selectedGranteeType !== value) {
      this.setState({ selectedGranteeType: value });
      this.clearSearchState();
    }
  }

  private onSearchTermChange = (value: string): void => {
    if (value){
      this.setState({ searchTerm: value });
      this.state.selectedGranteeType === GranteeType.user ? this.debounceSearchUsers(value) : this.debounceSearchGroups(value);
    } else {
      this.clearSearchState();
    }
  }

  // TODO: TFSID 895251 (open edit mode for the selected user/group)
  private onSearchResultSelect = (value: string): void => {
    this.clearSearchState();
  }

  private renderAddButton = (): JSX.Element => {
    return (
      <a data-tb-test-id="add-new-permission-button" onClick={ this.toggleButtonVisibility } tabIndex={0}>
        { Messages.addNewRule() }
      </a>
    );
  }

  private renderUserOrGroupSelector = (): JSX.Element => {
    const dropdownDOMID: string = 'add-permission-user-or-group-selector';
    const dropdownItems: ListItem[] = [
      { value: GranteeType.user, content: mapGranteeTypeToDisplayText()[GranteeType.user] },
      { value: GranteeType.group, content: mapGranteeTypeToDisplayText()[GranteeType.group] },
    ];
    const dropdownProps: SingleSelectDropdownProps = {
      list: <List items={dropdownItems} selectedValue={this.state.selectedGranteeType} />,
      onChange: this.onGranteeTypeChange,
      stackingRootId: dropdownDOMID,
      style: {
        width: '80px',
      },
      testId: 'add-permission-user-or-group-selector',
      type: DropdownButtonType.Outline,
      value: this.state.selectedGranteeType,
    };

    return (
      <span id={dropdownDOMID} className="tb-row-height-box tb-padded-right tb-popup-menu">
          <SingleSelectDropdown {...dropdownProps}>
            { mapGranteeTypeToDisplayText()[this.state.selectedGranteeType] }
          </SingleSelectDropdown>
        </span>
    );
  }

  private renderUserOrGroupSearch = (): JSX.Element => {
    const borderWidth: number = 1;
    const dropdownHeight: number = 120;

    const listProps: FilteringListProps = {
      items: this.state.searchResults,
      onFilterChange: this.onSearchTermChange,
      searchText: this.state.searchTerm,
      testId: 'add-permission-user-or-group-search',
      ...this.state.searchResults.length > 0 ? {
        onSelect: this.onSearchResultSelect,
        style: {
          margin: `-${CommonStyles.Sizes.SmallSpace + borderWidth}px`,
          height: `${dropdownHeight}px`,
          backgroundColor: CommonStyles.Colors.White,
          border: `${borderWidth}px solid ${CommonStyles.Colors.Grey203}`,
        },
      } : {
        searchPlaceholder: this.state.selectedGranteeType === GranteeType.user ? Messages.enterUserName() : Messages.enterGroupName(),
        style: {
          margin: `-${CommonStyles.Sizes.SmallSpace}px`,
          height: `${dropdownHeight}px`,
        },
      }
    };

    return (
      <span className="tb-row-height-box tb-padded-left">
        <FilteringList {...listProps} />
      </span>
    );
  }

  public render() {
    return this.state.isButton ? (
      <span className="tb-add-new-permission-container-react">
        { this.renderAddButton() }
      </span>
    ) : (
      <span className="tb-add-new-permission-container-react">
        { this.renderUserOrGroupSelector() }
        { this.renderUserOrGroupSearch() }
      </span>
    );
  }
}
